/*
  Shared behaviour for the Calming Sounds + Alarms 101 sections (Oct 2026):
  - one audio player for the whole page, so only one sound plays at a time
  - pill tabs ([data-sp-tabs])
  - sound tiles ([data-sp-sound]) and alarm tones ([data-sp-tone])
  - two-phase "Play a morning" demo ([data-sp-tp])
  - review carousel ([data-sp-reviews])
  - sound menu rows that open a library tab ([data-sp-jump])
  Loaded once per page by each section; guarded so it only initialises once.
*/
(function () {
  if (window.LoftieSoundPages) return;

  var audio = new Audio();
  audio.preload = 'none';
  var current = null;
  var listeners = [];

  function setProgress(el, p) { if (el) el.style.setProperty('--p', p); }

  function stop() {
    audio.pause();
    if (current) {
      current.classList.remove('is-playing');
      setProgress(current, 0);
      var btn = current.querySelector('[aria-pressed]');
      if (btn) btn.setAttribute('aria-pressed', 'false');
    }
    current = null;
    listeners.forEach(function (fn) { fn(); });
  }

  function play(el, src, loop) {
    if (!src) return;
    if (current === el) { stop(); return; }
    stop();
    current = el;
    audio.src = src;
    audio.loop = !!loop;
    audio.volume = 1;
    audio.currentTime = 0;
    var p = audio.play();
    if (p && p.catch) p.catch(function () {});
    el.classList.add('is-playing');
    var btn = el.querySelector('[aria-pressed]');
    if (btn) btn.setAttribute('aria-pressed', 'true');
  }

  audio.addEventListener('timeupdate', function () {
    if (current && audio.duration) setProgress(current, audio.currentTime / audio.duration);
  });
  audio.addEventListener('ended', function () { if (!audio.loop) stop(); });

  // Sound tiles + alarm tones (event delegation, works for sections added in the editor)
  document.addEventListener('click', function (e) {
    var playBtn = e.target.closest('[data-sp-sound] .sp-play');
    if (playBtn) {
      var tile = playBtn.closest('[data-sp-sound]');
      play(tile, tile.getAttribute('data-sp-sound'), true);
      return;
    }
    var tone = e.target.closest('[data-sp-tone]');
    if (tone) {
      var group = tone.parentElement;
      group.querySelectorAll('[data-sp-tone]').forEach(function (t) {
        t.setAttribute('aria-checked', t === tone ? 'true' : 'false');
      });
      var root = tone.closest('[data-sp-tp]');
      if (root && root._spRunning) root._spStop();
      play(tone, tone.getAttribute('data-sp-tone'), false);
      if (root) {
        var orb = root.querySelector('[data-sp-orb="' + tone.getAttribute('data-phase') + '"]');
        if (orb && current === tone) orb.classList.add('is-live');
      }
    }
  });

  // Sound menu rows ([data-sp-jump]) open the library tab with the same label, then scroll to it
  document.addEventListener('click', function (e) {
    var row = e.target.closest('[data-sp-jump]');
    if (!row) return;
    var want = row.getAttribute('data-sp-jump').trim().toLowerCase();
    var pill = Array.prototype.find.call(document.querySelectorAll('[data-sp-tab]'), function (p) {
      return p.textContent.trim().toLowerCase() === want;
    });
    if (!pill) return;
    e.preventDefault();
    pill.click();
    var target = pill.closest('section') || pill;
    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  });

  // Pill tabs
  function initTabs(scope) {
    scope.querySelectorAll('[data-sp-tabs]').forEach(function (root) {
      if (root._spTabs) return;
      root._spTabs = true;
      var pills = root.querySelectorAll('[data-sp-tab]');
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          var key = pill.getAttribute('data-sp-tab');
          pills.forEach(function (p) {
            var on = p === pill;
            p.classList.toggle('is-active', on);
            p.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          root.querySelectorAll('[data-sp-panel]').forEach(function (panel) {
            panel.classList.toggle('is-active', panel.getAttribute('data-sp-panel') === key);
          });
          stop();
        });
      });
    });
  }

  // Two-phase "Play a morning": phase 1 (6s) -> quiet gap (2.5s, sped up) -> phase 2 (6s)
  function initTwoPhase(scope) {
    scope.querySelectorAll('[data-sp-tp]').forEach(function (root) {
      if (root._spTp) return;
      root._spTp = true;
      var gap = parseInt(root.getAttribute('data-default-gap'), 10) || 9;
      var startHour = root.getAttribute('data-start-time') || '6:30';
      var track = root.querySelector('.sp-track');
      var msg = root.querySelector('.sp-runmsg');
      var runBtn = root.querySelector('[data-sp-run]');
      var gapLabel = root.querySelector('[data-sp-gap-label]');
      var idleMsg = msg ? msg.innerHTML : '';
      var runLabel = runBtn ? runBtn.innerHTML : '';
      var raf = null;
      // Only touch the aria-live message when its text changes, not every frame
      function say(html) { if (msg && msg.innerHTML !== html) msg.innerHTML = html; }
      var orbs = root.querySelectorAll('[data-sp-orb]');

      root.querySelectorAll('[data-sp-gap]').forEach(function (b) {
        b.addEventListener('click', function () {
          gap = parseInt(b.getAttribute('data-sp-gap'), 10);
          root.querySelectorAll('[data-sp-gap]').forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
          if (gapLabel) gapLabel.textContent = gap + ' min';
        });
      });

      function liveOrb(n) { orbs.forEach(function (o) { o.classList.toggle('is-live', o.getAttribute('data-sp-orb') === String(n)); }); }
      function reset() {
        if (raf) cancelAnimationFrame(raf);
        raf = null;
        root._spRunning = false;
        if (track) track.style.setProperty('--t', 0);
        if (msg) msg.innerHTML = idleMsg;
        if (runBtn) runBtn.innerHTML = runLabel;
        liveOrb(0);
      }
      root._spStop = function () { audio.pause(); reset(); };
      listeners.push(function () { liveOrb(0); });

      function time(addMin) {
        var parts = startHour.split(':');
        var h = parseInt(parts[0], 10), m = parseInt(parts[1], 10) + addMin;
        h += Math.floor(m / 60); m = m % 60;
        return h + ':' + (m < 10 ? '0' : '') + m + ' am';
      }
      function checked(phase) {
        return root.querySelector('[data-sp-tone][data-phase="' + phase + '"][aria-checked="true"]') ||
               root.querySelector('[data-sp-tone][data-phase="' + phase + '"]');
      }

      if (!runBtn) return;
      runBtn.addEventListener('click', function () {
        if (root._spRunning) { root._spStop(); return; }
        stop();
        var t1 = checked(1), t2 = checked(2);
        if (!t1 || !t2) return;
        root._spRunning = true;
        runBtn.textContent = runBtn.getAttribute('data-stop-label') || 'Stop';
        var start = performance.now(), phase = 0;
        audio.loop = false;
        audio.src = t1.getAttribute('data-sp-tone');
        audio.volume = 1;
        audio.play().catch(function () {});
        liveOrb(1);
        function step(now) {
          var s = (now - start) / 1000;
          if (s < 6) {
            track.style.setProperty('--t', (s / 6) * 0.38);
            audio.volume = Math.max(0.05, 1 - Math.max(0, s - 4) / 2);
            say('<b>' + time(0) + '</b> · ' + t1.textContent.trim() + ' plays softly, then fades');
          } else if (s < 8.5) {
            if (phase === 0) { phase = 1; audio.pause(); liveOrb(0); }
            track.style.setProperty('--t', 0.38 + ((s - 6) / 2.5) * 0.24);
            say('<b>Quiet</b> · ' + gap + ' minutes to wake up on your own');
          } else if (s < 14.5) {
            if (phase === 1) {
              phase = 2;
              audio.src = t2.getAttribute('data-sp-tone');
              audio.volume = 1;
              audio.play().catch(function () {});
              liveOrb(2);
            }
            track.style.setProperty('--t', 0.62 + ((s - 8.5) / 6) * 0.38);
            say('<b>' + time(gap) + '</b> · ' + t2.textContent.trim() + ': time to get up');
          } else {
            audio.pause();
            reset();
            return;
          }
          raf = requestAnimationFrame(step);
        }
        raf = requestAnimationFrame(step);
      });
    });
  }

  // Review carousel
  function initReviews(scope) {
    scope.querySelectorAll('[data-sp-reviews]').forEach(function (root) {
      if (root._spRev) return;
      root._spRev = true;
      var items = root.querySelectorAll('[data-sp-review]');
      var count = root.querySelector('[data-sp-count]');
      var i = 0;
      function show(n) {
        i = (n + items.length) % items.length;
        items.forEach(function (it, j) { it.hidden = j !== i; });
        if (count) count.textContent = (i + 1) + ' / ' + items.length;
      }
      var prev = root.querySelector('[data-sp-prev]'), next = root.querySelector('[data-sp-next]');
      if (prev) prev.addEventListener('click', function () { show(i - 1); });
      if (next) next.addEventListener('click', function () { show(i + 1); });
      show(0);
    });
  }

  function init(scope) {
    scope = scope || document;
    initTabs(scope);
    initTwoPhase(scope);
    initReviews(scope);
  }

  window.LoftieSoundPages = { init: init, stop: stop };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
